/**
 * usePointsQueries — TanStack Query hooks over the Plan 02-04 points API.
 *
 * Endpoints (mounted at /api/points by apps/api/src/app.ts):
 *   GET /api/points/balance/:memberId
 *   GET /api/points/history/:memberId?limit=N
 *   GET /api/points/personal-best/:memberId
 *
 * Cadence:
 *   - balance/history: staleTime 30s — wallet doesn't change frequently;
 *     a cache-busting WebSocket hook (Plan 15 celebrations) can call
 *     `qc.invalidateQueries({queryKey:['points', ...]})` when a chore is
 *     approved or a reward is redeemed.
 *   - personalBest: staleTime 60s — bucketed by ISO week, doesn't move
 *     mid-day on a typical wallet view.
 *
 * Following the Phase 1 / Plan 02-09 convention: direct `globalThis.fetch`
 * calls (no shared lib/api wrapper). Same `credentials: 'same-origin'` so
 * the auth cookie rides along.
 */
import { useQuery } from '@tanstack/react-query';

export type PointReason = 'chore_approved' | 'reward_redeemed' | 'adjustment' | 'cosmetic_purchased';

export interface PointTransactionRow {
  id: string;
  member_id: string;
  amount: number;
  reason: PointReason;
  chore_instance_id: string | null;
  reward_claim_id: string | null;
  note: string | null;
  created_at: string;
  created_by: string | null;
}

export interface PersonalBest {
  weekTag: string;
  points: number;
}

interface BalanceBody {
  memberId: string;
  balance: number;
}

interface HistoryBody {
  memberId: string;
  history: PointTransactionRow[];
}

interface PersonalBestBody {
  memberId: string;
  personalBest: PersonalBest | null;
}

async function fetchJson<T>(url: string): Promise<T> {
  const r = await globalThis.fetch(url, { credentials: 'same-origin' });
  if (!r.ok) {
    const e = new Error(`fetch_failed:${r.status}`) as Error & { status?: number };
    e.status = r.status;
    throw e;
  }
  return (await r.json()) as T;
}

export function useBalance(memberId: string, enabled = true) {
  return useQuery({
    queryKey: ['points', 'balance', memberId],
    queryFn: async () => {
      const body = await fetchJson<BalanceBody>(`/api/points/balance/${memberId}`);
      return body.balance;
    },
    enabled: enabled && memberId.length > 0,
    staleTime: 30_000,
  });
}

export function useHistory(memberId: string, limit = 30, enabled = true) {
  return useQuery({
    queryKey: ['points', 'history', memberId, limit],
    queryFn: async () => {
      const body = await fetchJson<HistoryBody>(
        `/api/points/history/${memberId}?limit=${limit}`,
      );
      return body.history;
    },
    enabled: enabled && memberId.length > 0,
    staleTime: 30_000,
  });
}

export function usePersonalBest(memberId: string, enabled = true) {
  return useQuery({
    queryKey: ['points', 'personal-best', memberId],
    queryFn: async () => {
      const body = await fetchJson<PersonalBestBody>(
        `/api/points/personal-best/${memberId}`,
      );
      return body.personalBest;
    },
    enabled: enabled && memberId.length > 0,
    staleTime: 60_000,
  });
}
